/**
 * VersichererBenachrichtigenDialog.tsx - P4-S7 steps 21-22.
 *
 * The checkbox dialog over the customer's declared insurances (F3): one row per relationship with
 * its Quelle, Sparte, Police, carrier, resolved recipient + level and a pre-selection, then a
 * per-carrier send of P-04.S22. Opening the dialog is a pure GET; only «Senden» writes. Both run
 * through TanStack Query, so a send refreshes the notification panel wherever it is mounted.
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Send } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import {
  BENACHRICHTIGUNG_MANDATE_BADGE_STATE,
  MANDATE_BADGE_STATE_LABEL,
  SEND_CARRIER_OUTCOME_LABEL,
  VERSICHERER_BENACHRICHTIGUNG_MAX_RELATIONSHIPS,
  sparteLabel,
} from '@brokly/shared';
import type {
  BenachrichtigungMandateStatus,
  SendVersichererBenachrichtigungResultDto,
  VersichererBenachrichtigungDialogDto,
} from '@brokly/shared';
import {
  useSendVersichererBenachrichtigung,
  useVersichererBenachrichtigungDialog,
} from './versicherer-benachrichtigung-queries';

const GATE_REASON: Record<string, string> = {
  not_sent: 'Das Analysemandat ist freigegeben, aber noch nicht zugestellt.',
  not_signed: 'Das Analysemandat ist zugestellt; die Unterzeichnung steht noch aus.',
  no_document: 'Zum Analysemandat liegt kein Dokument vor.',
  ok: '',
};

/**
 * The mandate chip: the Mandatskachel's words for this mandate (released -> «nicht erteilt», sent ->
 * «in Verarbeitung», signed -> «unterzeichnet»), from the same shared mapping and labels the badge
 * uses; never the raw status. The gate line above the rows carries the detail.
 */
function mandateChipLabel(status: BenachrichtigungMandateStatus | undefined): string | null {
  if (status === undefined) return null; // an api older than the field sends none: no chip
  return MANDATE_BADGE_STATE_LABEL[BENACHRICHTIGUNG_MANDATE_BADGE_STATE[status]] ?? null;
}

/** The German line for a failed send; a parallel send to the same insurers has its own. */
function sendErrorText(err: unknown): string {
  const code = (err as { code?: string } | null)?.code;
  if (code === 'notification_in_progress') {
    return 'Gerade läuft ein anderer Versand an diese Versicherer. Bitte versuchen Sie es in einigen Minuten erneut.';
  }
  return 'Der Versand ist fehlgeschlagen. Bitte versuchen Sie es erneut.';
}

export function VersichererBenachrichtigenDialog({
  subjectType,
  subjectId,
  onSent,
}: {
  subjectType: 'person' | 'firma';
  subjectId: string;
  onSent?: (result: SendVersichererBenachrichtigungResultDto) => void;
}) {
  const dialogQuery = useVersichererBenachrichtigungDialog(subjectType, subjectId);
  const send = useSendVersichererBenachrichtigung();
  const dialog = dialogQuery.data ?? null;
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<SendVersichererBenachrichtigungResultDto | null>(null);

  // Pre-tick once per loaded mandate; a refetch after the send does not re-tick what was sent.
  const [seededFor, setSeededFor] = useState<string | null>(null);
  useEffect(() => {
    if (dialog === null || seededFor === dialog.mandate_id) return;
    setChecked(new Set(dialog.rows.filter((r) => r.preselected).map((r) => r.relationship.id)));
    setSeededFor(dialog.mandate_id);
  }, [dialog, seededFor]);

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function onSend() {
    if (dialog === null) return;
    send.mutate(
      { mandateId: dialog.mandate_id, body: { relationship_ids: [...checked] } },
      {
        onSuccess: (res) => {
          setResult(res);
          setChecked(new Set());
          onSent?.(res);
        },
      },
    );
  }

  if (dialogQuery.isError) {
    const notFound = (dialogQuery.error as { status?: number } | null)?.status === 404;
    return (
      <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
        <AlertTriangle className="size-4" />
        {notFound
          ? 'Für diesen Kunden liegt kein Analysemandat vor.'
          : 'Der Dialog konnte nicht geladen werden.'}
      </p>
    );
  }
  if (dialog === null) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
        <Loader2 className="size-4 animate-spin" /> Lädt …
      </p>
    );
  }

  const busy = send.isPending;
  // The api refuses more than the cap in one send (400); say so here instead of failing on the click.
  const overCap = checked.size > VERSICHERER_BENACHRICHTIGUNG_MAX_RELATIONSHIPS;
  const canSend = dialog.gate.sendable && checked.size > 0 && !overCap;
  const carrierName = (uid: string): string =>
    dialog.rows.find((r) => r.carrier.carrier_uid === uid)?.carrier.name ?? uid;

  // P4-S8: a row is «neu erkannt» when it is late for this mandate and still 'neu' (as the process state counts it).
  const isLateNew = (r: VersichererBenachrichtigungDialogDto['rows'][number]): boolean =>
    r.relationship.late_for_mandate_id === dialog.mandate_id && r.relationship.status === 'neu';
  const lateCount = dialog.rows.filter(isLateNew).length;

  return (
    <section className="flex flex-col gap-3 rounded-lg border bg-card p-4" aria-label="Versicherer benachrichtigen">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            Versicherer benachrichtigen
            {lateCount > 0 && (
              <span
                className="rounded bg-sem-decide-bg px-1.5 py-0.5 text-xs font-medium text-sem-decide"
                data-testid="vb-neu-badge"
              >
                {lateCount} neu
              </span>
            )}
          </h3>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>Vorgang {dialog.vorgangs_ref}. Frist {dialog.frist_arbeitstage} Arbeitstage.</span>
            {mandateChipLabel(dialog.mandate_status) !== null && (
              <span className="rounded bg-muted px-1.5 py-0.5 font-medium" data-testid="vb-mandate-status">
                Analysemandat {mandateChipLabel(dialog.mandate_status)}
              </span>
            )}
          </p>
        </div>
        <Button
          size="sm"
          onClick={onSend}
          disabled={!canSend || busy}
          aria-busy={busy}
          data-testid="vb-senden"
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-3.5" />}
          Senden
        </Button>
      </div>

      {overCap && (
        <p className="rounded-md border border-sem-decide/40 bg-sem-decide-bg px-3 py-2 text-sm text-sem-decide" role="alert" data-testid="vb-over-cap">
          Höchstens {VERSICHERER_BENACHRICHTIGUNG_MAX_RELATIONSHIPS} Versicherungen pro Versand. Ausgewählt: {checked.size}.
          Bitte in mehreren Schritten senden.
        </p>
      )}

      {!dialog.gate.sendable && (
        <p className="rounded-md border border-sem-decide/40 bg-sem-decide-bg px-3 py-2 text-sm text-sem-decide" role="alert" data-testid="vb-gate">
          {GATE_REASON[dialog.gate.reason] ?? 'Versand nicht möglich.'}
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {dialog.rows.map((row) => (
          <li
            key={row.relationship.id}
            className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm"
            data-testid="vb-row"
          >
            <input
              type="checkbox"
              checked={checked.has(row.relationship.id)}
              disabled={!row.selectable}
              onChange={() => toggle(row.relationship.id)}
              aria-label={`Auswahl ${row.carrier.name ?? row.relationship.carrier_name ?? ''}`}
            />
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs">{row.source_label}</span>
            {isLateNew(row) && (
              <span
                className="rounded bg-sem-decide-bg px-1.5 py-0.5 text-xs text-sem-decide"
                data-testid="vb-neu-chip"
              >
                neu erkannt
              </span>
            )}
            <span className="font-medium">{row.carrier.name ?? row.relationship.carrier_name ?? 'Unbekannt'}</span>
            <span className="text-muted-foreground">{sparteLabel(row.relationship) ?? 'Sparte unbekannt'}</span>
            <span className="text-muted-foreground">
              {row.relationship.policy_number ?? 'ohne Policennummer'}
            </span>
            {row.resolution?.email != null && (
              <span className="text-xs text-muted-foreground">{row.resolution.email}</span>
            )}
            {row.state === 'sendable_fallback' && (
              <span className="rounded bg-sem-decide-bg px-1.5 py-0.5 text-xs text-sem-decide" data-testid="vb-badge-fallback">
                Hauptadresse
              </span>
            )}
            {row.state === 'no_address' && (
              <span className="text-xs text-sem-decide" data-testid="vb-no-address">
                Keine Adresse: Aufgabe wird erstellt
              </span>
            )}
            {row.state === 'already_notified' && (
              <span className="text-xs text-muted-foreground" data-testid="vb-already">
                Bereits benachrichtigt
                {row.open_notification
                  ? ` (${row.open_notification.status_label})`
                  : ''}
              </span>
            )}
            {row.state === 'unresolved_carrier' && (
              <span className="text-xs text-sem-decide" data-testid="vb-unresolved">
                Versicherer nicht zugeordnet
              </span>
            )}
          </li>
        ))}
      </ul>

      {send.isError && (
        <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertTriangle className="size-4" />
          {sendErrorText(send.error)}
        </p>
      )}

      {result !== null && (
        <div className="rounded-md border px-3 py-2 text-sm" role="status" data-testid="vb-result">
          <p>
            {result.sent} gesendet, {result.already_notified} bereits benachrichtigt, {result.skipped_no_address} ohne
            Adresse, {result.failed} fehlgeschlagen.
          </p>
          <ul className="mt-1 text-xs text-muted-foreground">
            {result.carriers.map((c) => (
              <li key={c.carrier_uid}>
                {carrierName(c.carrier_uid)}: {SEND_CARRIER_OUTCOME_LABEL[c.outcome]}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
